import React, { useEffect, useMemo, useState } from 'react'
import ZCard from '../../../../Card/Index'
import { Table, Typography } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { getMonthlySummary } from '../../../../../api/monthlySummary'
import { setMonthlySummaryData } from '../../../../../store/reducers/slices/monthlySummary.slice'
import { CurrencyFormatWithDecimal } from '../../../../../helper/CurrencyFormate'
import { CURRENCY } from '../../../../../utils/ConstantData'

const MonthlySummaryReport = () => {
    const [expandedTableData, setExpandedTableData] = useState([]);

    const { monthlySummaryData } = useSelector(({ monthlySummary }) => monthlySummary);
    const { selectedAccountId } = useSelector(({ reportDetails }) => reportDetails);
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchData = () => {
            getMonthlySummary(selectedAccountId).then((resp) => {
                dispatch(setMonthlySummaryData(resp))
            }).catch((error) => {
                console.log("monthly summary error", error);
            })
        }
        if (selectedAccountId) {
            fetchData();
        }
    }, [selectedAccountId])

    const getChildrenColumn = (prefix) => {
        return [
            {
                key: `${prefix}-amount`, title: `Amount (${CURRENCY})`, dataIndex: `${prefix}-amount`, className: 'no-wrap-column',
                render: (value) => (
                    <Typography.Text {...(parseFloat(value) < 0 ? { type: "danger" } : {})}>{parseFloat(value) > 0 ? CurrencyFormatWithDecimal(value, null, CURRENCY) : '-'}</Typography.Text>
                ),
            },
            { key: `${prefix}-txn`, title: "Txn count", dataIndex: `${prefix}-count`, width: 150 }
        ];
    }

    const { columns, data } = useMemo(() => {
        let columns = [];
        let data = [];
        if (Object.keys(monthlySummaryData).length > 0) {
            const firstColumn = [{ key: 1, title: "", children: [{ key: "blank-cell", width: "500px", title: "", dataIndex: "title", fixed: "left" }], fixed: "left" }]
            let _columns = monthlySummaryData.months.map((item, index) => {
                return ({ key: index, title: item, amount: `${item}-amount`, count: `${item}-count`, children: getChildrenColumn(item) })
            })
            columns = [...firstColumn, ..._columns];

            const firstRow = {
                key: "openingBalance",
                title: "Opening Balance",
                subCategories: null
            }

            const thirdRow = {
                key: "closingBalance",
                title: "Closing Balance",
                subCategories: null
            }

            const maxEODBalanceRow = {
                key: "maxEodBalance",
                title: "Max EOD Balance",
                subCategories: null
            }

            const minEODBalanceRow = {
                key: "minEodBalance",
                title: "Min EOD Balance",
                subCategories: null
            }

            const avgEODBalanceRow = {
                key: "avgEodBalance",
                title: "Avg EOD Balance",
                subCategories: null
            }

            monthlySummaryData.openingBalance.forEach((item) => {
                firstRow[`${item.month}-amount`] = item.amount;
                firstRow[`${item.month}-count`] = "";
            })

            monthlySummaryData.closingBalance.forEach((item) => {
                thirdRow[`${item.month}-amount`] = item.amount;
                thirdRow[`${item.month}-count`] = "";
            })

            monthlySummaryData.maxEodBalance.forEach((item) => {
                maxEODBalanceRow[`${item.month}-amount`] = item.amount;
                maxEODBalanceRow[`${item.month}-count`] = "";
            })

            monthlySummaryData.minEodBalance.forEach((item) => {
                minEODBalanceRow[`${item.month}-amount`] = item.amount;
                minEODBalanceRow[`${item.month}-count`] = "";
            })

            monthlySummaryData.avgEodBalance.forEach((item) => {
                avgEODBalanceRow[`${item.month}-amount`] = item.amount;
                avgEODBalanceRow[`${item.month}-count`] = "";
            })

            data.push(firstRow);
            for (let key in monthlySummaryData.dataDeposits) {
                let row = { title: key, key: `${key} deposit`.replace(/ /g, "-") };
                monthlySummaryData.dataDeposits[key].data.map((item) => {
                    row[`${item.month}-amount`] = item.amount;
                    row[`${item.month}-count`] = item.count;
                })
                row.subCategories = monthlySummaryData?.dataDeposits[key]?.subCategories || null;
                data.push(row)
            }

            for (let key in monthlySummaryData.dataWithdrawals) {
                let row = { title: key, key: `${key} withdrawals`.replace(/ /g, "-") };
                monthlySummaryData.dataWithdrawals[key].data.map((item) => {
                    row[`${item.month}-amount`] = item.amount;
                    row[`${item.month}-count`] = item.count;
                })
                row.subCategories = monthlySummaryData?.dataWithdrawals[key]?.subCategories || null
                data.push(row)
            }
            data.push(thirdRow);
            data.push(maxEODBalanceRow);
            data.push(minEODBalanceRow);
            data.push(avgEODBalanceRow);
        }
        return { columns, data }
    }, [monthlySummaryData]);

    return (
        <ZCard>
            <Table
                columns={columns}
                bordered
                dataSource={data}
                scroll={{ x: true }}
                pagination={false}
                expandable={{
                    expandedRowRender: (record) => {
                        if (record.subCategories && Object.keys(record.subCategories).length > 0) {
                            let tableData = [];
                            for (let key in record.subCategories) {
                                let row = { title: key, key: key };
                                record.subCategories[key].forEach((item) => {
                                    row[`${item.month}-amount`] = item?.amount || 0;
                                    row[`${item.month}-count`] = item.count;
                                })
                                tableData.push(row);
                            }
                            return <Table columns={columns} bordered rowClassName={'table-header-row'} dataSource={tableData} pagination={false} scroll={{ x: true }} />
                        }
                    },
                    rowExpandable: (record) => {
                        return (record.subCategories && Object.keys(record.subCategories).length > 0)
                    },
                }}
                rowClassName={(record) => {
                    if (
                        record.title === "Opening Balance" ||
                        record.title === "Deposits" ||
                        record.title === "Withdrawals" ||
                        record.title === "Closing Balance" ||
                        record.title === "Max EOD Balance" ||
                        record.title === "Min EOD Balance" ||
                        record.title === "Avg EOD Balance"
                    ) {
                        return "table-header-row fw-500"
                    } else if (record.subCategories && Object.keys(record.subCategories).length > 0) {
                        return "table-header-row"
                    }
                }}
                onChange={(value) => console.log("expandTable", value)}
            />
        </ZCard>
    )
}

export default MonthlySummaryReport